import React, { useEffect, useState } from 'react';
import { useMediaQuery, useTheme, Tabs, Tab, Grid2, Button, Typography, Box } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import { Appointment } from '../../types';
import { parseTime, convertTo24HourString, formatTime } from '../../time/timeUtils';

interface TimeSlotPickerProps {
    appointment: Appointment;
    handleAppointment: (appointment: Appointment) => void;
    updateAppointment: boolean;
    availableSlots: string[];
    goToNextStep: () => void;
    navigateToNextStep: boolean;
    onSlotSelect?: (slot: string) => boolean;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
    appointment,
    handleAppointment,
    updateAppointment,
    availableSlots,
    goToNextStep,
    navigateToNextStep,
    onSlotSelect,
}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [selectedSlot, setSelectedSlot] = useState<string | null>(appointment.appointmentTime);
    const [selectedTab, setSelectedTab] = useState<'AM' | 'PM'>('AM');

    useEffect(() => {
        setSelectedSlot(appointment.appointmentTime || null);
    }, [appointment.appointmentTime]);

    const chunkSlots = (slots: string[], chunkSize: number) => {
        const chunks: string[][] = [];
        for (let i = 0; i < slots.length; i += chunkSize) {
            chunks.push(slots.slice(i, i + chunkSize));
        }
        return chunks;
    };

    // Split available slots into AM and PM
    const amSlots = availableSlots.filter(slot => parseTime(slot)?.hour() !== undefined && parseTime(slot)!.hour() < 12);
    const pmSlots = availableSlots.filter(slot => parseTime(slot)?.hour() !== undefined && parseTime(slot)!.hour() >= 12);
    const amChunks = chunkSlots(amSlots, 9);
    const pmChunks = chunkSlots(pmSlots, 9);

    // Open on PM when the day has no morning times left (late in the day).
    const hasAmSlots = amSlots.length > 0;
    const hasPmSlots = pmSlots.length > 0;
    useEffect(() => {
        if (!hasAmSlots && hasPmSlots) setSelectedTab('PM');
    }, [hasAmSlots, hasPmSlots]);
    const tabSlots = selectedTab === 'AM' ? amSlots : pmSlots;

    // Handle tab change
    const handleTabChange = (_event: React.SyntheticEvent, newValue: 'AM' | 'PM') => {
        setSelectedTab(newValue);
    };

    // Validate slot selection first, then commit appointment state.
    // In Any Available flow, onSlotSelect also resolves/assigns an employee and updates appointment;
    // avoid overwriting that state with a second generic appointment write.
    const handleSlotSelect = (slot: string) => {
        if (onSlotSelect && !onSlotSelect(slot)) {
            return;
        }

        setSelectedSlot(slot);

        if (updateAppointment && !onSlotSelect) {
            const convertedTime = convertTo24HourString(parseTime(slot));
            handleAppointment({ ...appointment, appointmentTime: convertedTime });
        }

        if (navigateToNextStep) {
            goToNextStep();
        }
    };

    return (
        <>
            <Typography
                variant="overline"
                align="center"
                display="block"
                sx={{ marginBottom: 2 }}
            >
                SELECT TIME
            </Typography>

            {isSmallScreen && (
                <>
                    {/* AM Section */}
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                        <WbSunnyIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#FFC107' }} /> Morning
                    </Typography>
                    {amSlots.length > 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                overflowX: 'auto',
                                scrollSnapType: 'x mandatory',
                                gap: 2,
                                pb: 1,
                                px: 0.5,
                            }}
                        >
                            {amChunks.map((chunk, chunkIndex) => (
                            <Box
                                key={chunkIndex}
                                sx={{
                                    flex: '0 0 100%',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 120px)',
                                    gap: 1,
                                    scrollSnapAlign: 'start',
                                    justifyContent: 'center',
                                    justifyItems: 'center',
                                    alignContent: 'start',
                                }}
                            >
                                {chunk.map((slot) => (
                                    <Button
                                        key={slot}
                                        variant="contained"
                                        onClick={() => handleSlotSelect(slot)}
                                        sx={(theme) => {
                                            const isSelected = selectedSlot === slot;
                                            const backgroundColor = isSelected
                                                ? theme.palette.secondary.main
                                                : theme.palette.common.black;
                                            const hoverBackgroundColor = isSelected
                                                ? theme.palette.secondary.dark
                                                : theme.palette.grey[700];

                                            return {
                                                width: '120px',
                                                height: '48px',
                                                borderRadius: 2,
                                                backgroundColor,
                                                color: theme.palette.getContrastText(backgroundColor),
                                                '&:hover': {
                                                    backgroundColor: hoverBackgroundColor,
                                                    color: theme.palette.getContrastText(hoverBackgroundColor),
                                                },
                                            };
                                        }}
                                    >
                                        {formatTime(slot)}
                                    </Button>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            No available morning slots.
                        </Typography>
                    )}

                    {/* PM Section */}
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 3 }}>
                        <WbTwilightIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#FF5722' }} />Afternoon/Evening
                    </Typography>
                    {pmSlots.length > 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                overflowX: 'auto',
                                scrollSnapType: 'x mandatory',
                                gap: 2,
                                pb: 1,
                                px: 0.5,
                            }}
                        >
                            {pmChunks.map((chunk, chunkIndex) => (
                            <Box
                                key={chunkIndex}
                                sx={{
                                    flex: '0 0 100%',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 120px)',
                                    gap: 1,
                                    scrollSnapAlign: 'start',
                                    justifyContent: 'center',
                                    justifyItems: 'center',
                                    alignContent: 'start',
                                }}
                            >
                                {chunk.map((slot) => (
                                    <Button
                                        key={slot}
                                        variant="contained"
                                        onClick={() => handleSlotSelect(slot)}
                                        sx={(theme) => {
                                            const isSelected = selectedSlot === slot;
                                            const backgroundColor = isSelected
                                                ? theme.palette.secondary.main
                                                : theme.palette.common.black;
                                            const hoverBackgroundColor = isSelected
                                                ? theme.palette.secondary.dark
                                                : theme.palette.grey[700];

                                            return {
                                                width: '120px',
                                                height: '48px',
                                                borderRadius: 2,
                                                backgroundColor,
                                                color: theme.palette.getContrastText(backgroundColor),
                                                '&:hover': {
                                                    backgroundColor: hoverBackgroundColor,
                                                    color: theme.palette.getContrastText(hoverBackgroundColor),
                                                },
                                            };
                                        }}
                                    >
                                        {formatTime(slot)}
                                    </Button>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="error">
                            No available afternoon/evening slots.
                        </Typography>
                    )}
                </>
            )}

            {/* Tabs for AM and PM */}
            {!isSmallScreen && (
                <>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        centered
                        sx={{
                            marginBottom: 2,
                            '& .MuiTabs-indicator': { backgroundColor: 'black' },
                            '& .MuiTab-root': { color: 'black', fontWeight: 'bold' },
                            '& .Mui-selected': {
                                color: 'white !important',  // Set text color to red when selected
                                backgroundColor: 'black', // Add a red background highlight
                            },
                        }}
                    >
                        <Tab icon={<WbSunnyIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#FFC107' }} />} iconPosition="start" label="AM" value="AM" />
                        <Tab icon={<WbTwilightIcon sx={{ color: '#FF5722' }} />} iconPosition="start" label="PM" value="PM" />
                    </Tabs>

                    {tabSlots.length === 0 && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                            {!hasAmSlots && !hasPmSlots
                                ? 'No times left on this day.'
                                : selectedTab === 'AM' ? 'No morning times. Try PM.' : 'No afternoon or evening times. Try AM.'}
                        </Typography>
                    )}
                    <Grid2 container spacing={3} px={6} justifyContent="center">
                        {tabSlots.map((slot, index) => (
                            <Grid2
                                size={{ xs: 4 }}
                                key={index}
                                sx={{ display: 'flex', justifyContent: 'center' }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => handleSlotSelect(slot)}
                                    sx={(theme) => {
                                        const isSelected = selectedSlot === slot;
                                        const backgroundColor = isSelected
                                            ? theme.palette.secondary.main
                                            : theme.palette.common.black;
                                        const hoverBackgroundColor = isSelected
                                            ? theme.palette.secondary.dark
                                            : theme.palette.grey[700];

                                        return {
                                            minWidth: 100,
                                            borderRadius: 2,
                                            backgroundColor,
                                            color: theme.palette.getContrastText(backgroundColor),
                                            '&:hover': {
                                                backgroundColor: hoverBackgroundColor,
                                                color: theme.palette.getContrastText(hoverBackgroundColor),
                                            },
                                        };
                                    }}
                                >
                                    {formatTime(slot)}
                                </Button>
                            </Grid2>
                        ))}
                    </Grid2>
                </>
            )}
            <Typography
                variant="caption"
                align="center"
                display="block"
                sx={{
                    mt: 4,
                    px: 2,
                    py: 1,
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    textAlign: 'center',
                    maxWidth: '500px',
                    mx: 'auto',
                }}
            >
                If these times do not work for you, please call us and we will accommodate you.
                <br />
                Thank you!
            </Typography>

        </>
    );
};

export default TimeSlotPicker;
